import React, { useRef, useState } from 'react';
import { submitPartnerApplication } from '../services/partnerApiClient';
import '../styles/Parceiros.css';

const PARTNER_TYPES = [
  { value: 'accountant', title: 'Contadores', text: 'Indique uma gestão mais organizada e acompanhe os clientes da sua carteira.', icon: 'fa-calculator' },
  { value: 'sales_channel', title: 'Representantes e afiliados', text: 'Indique o Nivora, acompanhe sua carteira e receba comissões conforme a regra aprovada.', icon: 'fa-handshake' },
  { value: 'payment_gateway', title: 'Gateways de pagamento', text: 'Aproxime sua solução da operação financeira do pequeno negócio.', icon: 'fa-credit-card' },
  { value: 'freight', title: 'Frete e logística', text: 'Ajude estabelecimentos locais a organizar entregas e acompanhamento.', icon: 'fa-truck-fast' }
];

const EMPTY_FORM = {
  partnerType: 'accountant',
  companyName: '',
  contactName: '',
  email: '',
  phone: '',
  website: '',
  message: ''
};

export default function ParceirosPublico() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const applicationAttemptKey = useRef('');
  const logo = `${process.env.PUBLIC_URL || ''}/assets/nivora-logo.png`;

  const update = (field) => (event) => {
    applicationAttemptKey.current = '';
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setFeedback(null);
    try {
      if (!applicationAttemptKey.current) {
        const nonce = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
        applicationAttemptKey.current = `partner-application-${nonce}`;
      }
      const result = await submitPartnerApplication({
        ...form,
        companyName: form.companyName.trim(),
        contactName: form.contactName.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        website: form.website.trim() || undefined,
        message: form.message.trim() || undefined
      }, { idempotencyKey: applicationAttemptKey.current });
      setFeedback({
        type: 'success',
        text: `Recebemos seu contato${result?.applicationId ? ` (${result.applicationId})` : ''}. Nossa equipe retornará pelos dados informados.`
      });
      applicationAttemptKey.current = '';
      setForm(EMPTY_FORM);
    } catch (error) {
      setFeedback({ type: 'error', text: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = (event) => {
    event.preventDefault();
    document.getElementById('quero-ser-parceiro')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="partner-page partner-public-page">
      <header className="partner-topbar">
        <a className="partner-brand" href="/" aria-label="Nivora ERP - página inicial">
          <img src={logo} alt="" aria-hidden="true" />
          <span>Nivora ERP</span>
          <small>Parceiros</small>
        </a>
        <nav aria-label="Navegação de parceiros">
          <a href="/">Conhecer o ERP</a>
          <a href="/parceiros/login">Já sou parceiro</a>
          <a className="partner-nav-cta" href="#quero-ser-parceiro" onClick={scrollToForm}>Quero ser parceiro</a>
        </nav>
      </header>

      <main>
        <section className="partner-hero">
          <div className="partner-hero-copy">
            <span className="partner-eyebrow">Ecossistema Nivora</span>
            <h1>Leve mais valor para sua carteira de pequenos negócios.</h1>
            <p>
              Contadores e empresas de tecnologia podem indicar clientes, acompanhar oportunidades e participar das
              integrações que forem disponibilizadas no Nivora ERP.
            </p>
            <div className="partner-actions">
              <a className="partner-button partner-button-primary" href="#quero-ser-parceiro" onClick={scrollToForm}>
                Conversar sobre parceria <i className="fas fa-arrow-right" aria-hidden="true"></i>
              </a>
              <a className="partner-button partner-button-secondary" href="/parceiros/login">
                Acessar meu painel
              </a>
            </div>
          </div>
          <aside className="partner-hero-panel" aria-label="Como funciona">
            <span>Uma parceria simples</span>
            <ol>
              <li><b>1</b><div><strong>Apresente-se</strong><small>Conte sua área e o perfil da sua carteira.</small></div></li>
              <li><b>2</b><div><strong>Alinhamos o modelo</strong><small>Validamos indicação, suporte e possível integração.</small></div></li>
              <li><b>3</b><div><strong>Acompanhe</strong><small>Consulte clientes indicados no portal privado.</small></div></li>
            </ol>
          </aside>
        </section>

        <section className="partner-section" aria-labelledby="partner-types-title">
          <div className="partner-section-heading">
            <span className="partner-eyebrow">Quem pode participar</span>
            <h2 id="partner-types-title">Parcerias que fazem sentido na rotina do lojista.</h2>
            <p>Começamos pelo relacionamento comercial e evoluímos para integrações apenas quando houver aderência técnica.</p>
          </div>
          <div className="partner-type-grid">
            {PARTNER_TYPES.map((type) => (
              <article key={type.value}>
                <i className={`fas ${type.icon}`} aria-hidden="true"></i>
                <h3>{type.title}</h3>
                <p>{type.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="partner-application" id="quero-ser-parceiro">
          <div className="partner-application-copy">
            <span className="partner-eyebrow">Vamos conversar</span>
            <h2>Conte como você ajuda pequenos negócios.</h2>
            <p>
              Avaliamos cada parceria de forma objetiva. O envio não cria obrigação comercial nem promete uma integração;
              ele abre o contato com nosso time.
            </p>
            <div className="partner-trust-note">
              <i className="fas fa-shield-halved" aria-hidden="true"></i>
              <span>Seus dados serão usados somente para avaliar e responder esta candidatura.</span>
            </div>
          </div>

          <form className="partner-form" onSubmit={submit}>
            <div className="partner-form-grid">
              <label>
                Tipo de parceria
                <select value={form.partnerType} onChange={update('partnerType')} required>
                  {PARTNER_TYPES.map((type) => <option key={type.value} value={type.value}>{type.title}</option>)}
                  <option value="other">Outro</option>
                </select>
              </label>
              <label>
                Empresa
                <input value={form.companyName} onChange={update('companyName')} minLength="2" maxLength="160" required />
              </label>
              <label>
                Seu nome
                <input value={form.contactName} onChange={update('contactName')} autoComplete="name" minLength="2" maxLength="120" required />
              </label>
              <label>
                E-mail profissional
                <input type="email" value={form.email} onChange={update('email')} autoComplete="email" maxLength="180" required />
              </label>
              <label>
                Telefone / WhatsApp
                <input type="tel" value={form.phone} onChange={update('phone')} autoComplete="tel" minLength="8" maxLength="30" required />
              </label>
              <label>
                Site <small>(opcional)</small>
                <input type="url" value={form.website} onChange={update('website')} placeholder="https://" maxLength="240" />
              </label>
            </div>
            <label>
              Como imagina esta parceria? <small>(opcional)</small>
              <textarea value={form.message} onChange={update('message')} rows="4" maxLength="1200" />
            </label>
            {feedback && (
              <div className={`partner-feedback is-${feedback.type}`} role={feedback.type === 'error' ? 'alert' : 'status'}>
                <i className={`fas ${feedback.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'}`} aria-hidden="true"></i>
                {feedback.text}
              </div>
            )}
            <button className="partner-button partner-button-primary" type="submit" disabled={submitting}>
              {submitting ? 'Enviando...' : 'Enviar candidatura'}
            </button>
          </form>
        </section>
      </main>

      <footer className="partner-footer">
        <span><strong>Nivora Parceiros</strong> · relacionamento e integrações para pequenos negócios.</span>
        <div><a href="/">Página inicial</a><a href="/parceiros/login">Área do parceiro</a></div>
      </footer>
    </div>
  );
}
